import type { z } from 'zod';

/**
 * Deployed fail-fast for missing secrets (spec #16564 §6): on every deployed
 * tier — production, staging, and development (what `main` deploys to the
 * Vercel preview target) — a required secret resolving to nothing must fail
 * the build/boot instead of silently signing with '' or a known constant.
 *
 * Scoped to Vercel builds and runtime (`VERCEL` is set): that is where the
 * pulled environment must be complete. CI test/build steps run with
 * DEPLOYMENT_ENVIRONMENT=production on the stable branch but without the
 * production secrets — the guard must not fire there. Also skipped in the
 * browser (client bundles legitimately carry only NEXT_PUBLIC_* values) and
 * in the native app (a user-facing crash is the wrong failure mode there —
 * the build-time generate-app-dot-env warning is its guard). Keys declared
 * `.optional()` in the zod schema are configuration-dependent (e.g. the
 * replica database credentials) and are exempt; a required secret uses
 * `.default('')`.
 */

type SecretMetaLike = {
  sensitivity?: string;
  tiers?: string[];
  status?: string;
};

// .meta() lives on the wrapper it was called on: walk the unwrap chain.
function secretMetaOf(type: unknown): SecretMetaLike | undefined {
  let current: any = type;
  for (let guard = 0; current && guard < 10; guard++) {
    const meta =
      typeof current.meta === 'function' ? current.meta() : undefined;
    if (meta?.secret) {
      return meta.secret as SecretMetaLike;
    }
    const next =
      typeof current.unwrap === 'function' ? current.unwrap() : undefined;
    if (!next || next === current) {
      break;
    }
    current = next;
  }
  return undefined;
}

function isOptional(type: unknown): boolean {
  let current: any = type;
  for (let guard = 0; current && guard < 10; guard++) {
    if (
      current.def?.type === 'optional' ||
      current._def?.typeName === 'ZodOptional'
    ) {
      return true;
    }
    const next =
      typeof current.unwrap === 'function' ? current.unwrap() : undefined;
    if (!next || next === current) {
      break;
    }
    current = next;
  }
  return false;
}

export function assertDeployedSecrets(
  schema: z.ZodObject<z.ZodRawShape>,
  result: { success: boolean; data?: Record<string, unknown> | undefined },
  packageName: string,
  processEnv: Record<string, string | undefined> = process.env,
): void {
  if (typeof window !== 'undefined') {
    return;
  }
  if (!processEnv.VERCEL) {
    return;
  }
  const tier =
    processEnv.DEPLOYMENT_ENVIRONMENT || processEnv.NEXT_PUBLIC_PLATFORM;
  if (tier !== 'production' && tier !== 'staging' && tier !== 'development') {
    return;
  }

  // A parse failure must fail the deploy with its own message: reporting
  // every secret as missing (data is undefined) would mask the real error,
  // which the env.ts already logged.
  if (!result.success) {
    throw new Error(
      `${packageName} - invalid environment variables on the ${tier} tier (see the error logged above)`,
    );
  }
  const { data } = result;

  const missing: string[] = [];
  for (const [key, type] of Object.entries(schema.shape)) {
    const meta = secretMetaOf(type);
    if (!meta || meta.sensitivity !== 'secret') {
      continue;
    }
    if ((meta.status ?? 'active') !== 'active') {
      continue;
    }
    if (meta.tiers && !meta.tiers.includes(tier)) {
      continue;
    }
    if (isOptional(type)) {
      continue;
    }
    const value = data?.[key];
    if (value === undefined || value === '') {
      missing.push(key);
    }
  }
  if (missing.length > 0) {
    throw new Error(
      `${packageName} - missing required secrets on the ${tier} tier: ${missing.join(', ')} (see spec #16564 §6)`,
    );
  }
}
